import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Box, Chip, Tooltip, Typography } from "@mui/material";

// Inline format that makes {{placeholders}} look like variables inside the editor.
// Output is <span class="email-var">{{name}}</span>; the backend sanitizer keeps the text and drops the class.
const Inline = Quill.import("blots/inline") as any;
class VariableBlot extends Inline {
  static blotName = "variable";
  static tagName = "span";
  static className = "email-var";
}
try {
  Quill.register(VariableBlot, true);
} catch {
  // already registered (hot reload)
}

export interface EmailVariable {
  key: string;
  label: string;
  hint: string;
  disabled?: boolean;
  disabledHint?: string;
}

export interface EmailVariableGroup {
  title: string;
  items: EmailVariable[];
}

export interface EmailRichTextEditorHandle {
  insertVariable: (key: string) => void;
}

interface EmailRichTextEditorProps {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  variableGroups?: EmailVariableGroup[];
}

const FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "size",
  "list",
  "bullet",
  "indent",
  "align",
  "blockquote",
  "link",
  "variable",
];

/**
 * Rich text editor for email bodies. Emits HTML; the backend sanitizes it and converts
 * Quill's class-based formatting to inline styles before sending.
 */
const EmailRichTextEditor = forwardRef<EmailRichTextEditorHandle, EmailRichTextEditorProps>(
  ({ label, value, onChange, minHeight = 240, variableGroups }, ref) => {
    const quillRef = useRef<ReactQuill>(null);

    // Must be stable between renders or Quill re-mounts and loses focus
    const modules = useMemo(
      () => ({
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "link"],
          ["clean"],
        ],
      }),
      []
    );

    const insertVariable = (key: string) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      // true = focus the editor and use the last cursor position (or the start if there never was one)
      const range = quill.getSelection(true);
      const token = `{{${key}}}`;
      if (range.length > 0) quill.deleteText(range.index, range.length, "user");
      quill.insertText(range.index, token, { variable: true }, "user");
      // unformatted space after it so further typing is not styled as part of the variable
      quill.insertText(range.index + token.length, " ", { variable: false }, "user");
      quill.setSelection(range.index + token.length + 1, 0, "user");
    };

    useImperativeHandle(ref, () => ({ insertVariable }));

    return (
      <Box
        sx={{
          "& .ql-container": { minHeight, fontSize: 15, fontFamily: "inherit" },
          "& .ql-editor": { minHeight },
          "& .ql-toolbar": { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
          "& .ql-container.ql-snow": { borderBottomLeftRadius: 4, borderBottomRightRadius: 4 },
          "& .ql-editor .email-var": {
            backgroundColor: "rgba(116, 52, 68, 0.12)",
            color: "#743444",
            border: "1px dashed #743444",
            borderRadius: "4px",
            padding: "0 4px",
            fontFamily: "Consolas, Menlo, monospace",
            fontSize: "0.9em",
            whiteSpace: "nowrap",
          },
        }}
      >
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {label}
          </Typography>
        )}

        {variableGroups && variableGroups.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Click a variable to insert it where your cursor is. It is replaced with each recipient's own details when sent.
            </Typography>
            {variableGroups.map((group) => (
              <Box key={group.title} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ minWidth: 70 }}>
                  {group.title}
                </Typography>
                {group.items.map((v) => (
                  <Tooltip key={v.key} title={v.disabled ? v.disabledHint || v.hint : v.hint}>
                    <span>
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={v.label}
                        disabled={v.disabled}
                        // keep the editor selection while clicking
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertVariable(v.key)}
                        sx={{ fontFamily: "Consolas, Menlo, monospace", fontSize: 12 }}
                      />
                    </span>
                  </Tooltip>
                ))}
              </Box>
            ))}
          </Box>
        )}

        <ReactQuill ref={quillRef} theme="snow" value={value} onChange={onChange} modules={modules} formats={FORMATS} />
      </Box>
    );
  }
);

EmailRichTextEditor.displayName = "EmailRichTextEditor";

export default EmailRichTextEditor;
